import React, { useCallback, useEffect, useState } from 'react';
import styles from './BulkBindRosters.module.css';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import BindMatrix from '../BindMatrix/BindMatrix';
import useApi from '../../hooks/useApi.hook';
import useProfessionRows from '../../hooks/useProfessionRows.hook';
import { getRosterBindsUrl, applyRosterBindsUrl } from '../../helpers/constants';

const toKey = (b) => `${b.profession_id}-${b.roster_code}`;

const fromKey = (key) => {
	const [professionId, ...code] = key.split('-');
	return { profession_id: Number(professionId), roster_code: code.join('-') };
};

export default function BulkBindRosters({ professions }) {
	const api = useApi();
	const [rosters, setRosters] = useState([]);
	const [baseState, setBaseState] = useState(() => new Set());
	const [currentState, setCurrentState] = useState(() => new Set());
	const [initialLoading, setInitialLoading] = useState(true);
	const [loading, setLoading] = useState(false);
	const [saveOk, setSaveOk] = useState(false);
	const [resetOk, setResetOk] = useState(false);
	const [errorApi, setErrorApi] = useState(undefined);
	const rowItems = useProfessionRows(professions);
	const isDirty = currentState.size !== baseState.size
		|| [...currentState].some(key => !baseState.has(key));

	const applyState = useCallback((data) => {
		setRosters(data.rosters.map(r => ({ id: r.code, label: r.title })));
		const binds = new Set(data.binds.map(toKey));
		setBaseState(binds);
		setCurrentState(new Set(binds));
	}, []);

	const getErrorText = (e) =>
		e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка';

	useEffect(() => {
		api.get(getRosterBindsUrl)
			.then(({ data }) => applyState(data))
			.catch(e => setErrorApi(getErrorText(e)))
			.finally(() => setInitialLoading(false));
	}, []);

	const toggleCell = (rowId, colId) => {
		const key = `${rowId}-${colId}`;
		setCurrentState(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const toggleAll = (colId) => {
		const allChecked = rowItems.every(row => currentState.has(`${row.id}-${colId}`));
		setCurrentState(prev => {
			const next = new Set(prev);
			rowItems.forEach(row => {
				const key = `${row.id}-${colId}`;
				if (allChecked) next.delete(key);
				else next.add(key);
			});
			return next;
		});
	};

	const saveChanges = async () => {
		const add = [...currentState].filter(k => !baseState.has(k)).map(fromKey);
		const remove = [...baseState].filter(k => !currentState.has(k)).map(fromKey);

		setLoading(true);
		setErrorApi(undefined);
		try {
			const { data } = await api.post(applyRosterBindsUrl, { add, remove });
			applyState(data);
			setSaveOk(true);
			setTimeout(() => setSaveOk(false), 1500);
		} catch (e) {
			setErrorApi(getErrorText(e));
		} finally {
			setLoading(false);
		}
	};

	const resetChanges = () => {
		setCurrentState(new Set(baseState));
		setResetOk(true);
		setTimeout(() => setResetOk(false), 1500);
	};

	if (initialLoading) {
		return <div className={styles.loadingWrap}><Spinner /></div>;
	}

	return (
		<div className={styles.container}>
			<ErrorMessage error={errorApi} onClose={() => setErrorApi(undefined)} />
			<BindMatrix
				loading={loading}
				rows={rowItems}
				columns={rosters}
				currentState={currentState}
				baseState={baseState}
				toggleCell={toggleCell}
				toggleAll={toggleAll}
			/>
			<div className={styles.actions}>
				<Button
					onClick={saveChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${saveOk ? styles.btnOk : ''}`}
				>
					{saveOk ? '✓ Сохранено' : 'Сохранить'}
				</Button>
				<Button
					onClick={resetChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${resetOk ? styles.btnOk : ''}`}
				>
					{resetOk ? '✓ Сброшено' : 'Сбросить'}
				</Button>
			</div>
		</div>
	);
}
